import { captureException, captureMessage } from '@sentry/sveltekit';
import { error, fail } from '@sveltejs/kit';
import { writable, type Writable } from 'svelte/store';
import type { ZodError } from 'zod';
import { PUBLIC_CLOUDFLARE_ACCOUNT_HASH } from '$env/static/public';
import { env } from '$env/dynamic/public';
import type { lots } from '@prisma/client';
import { websocketClient } from './WebSocket';
import { browser } from '$app/environment';
import type { JsonValue } from './generated/prisma-client/internal/prismaNamespace';

export type IError = {
  type?: string;
  detail: string;
  metadata?: Record<string, string>;
};

export type IRef = {
  id: any;
};

export function formatMoney(amount: number | bigint | null | undefined, currency: string = 'PYG') {
  if (amount == null || amount == undefined) return '';

  const formatted = new Intl.NumberFormat('es-PY', {
    style: 'currency',
    currency
  }).format(amount);

  return formatted.replace('PYG', 'Gs.');
}

export const formDataToObject = (form: HTMLFormElement | FormData) => {
  const data = form instanceof FormData ? form : new FormData(form);
  const obj = Object.fromEntries(data.entries());
  const res = {} as object;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const set = (obj: any, path: string | string[], value: any) => {
    const parts = !Array.isArray(path) ? path.split('.') : path;
    const last = parts.pop();
    const parent = parts.reduce((obj, part) => (obj[part] ??= {}), obj);

    if (last) {
      parent[last] = value;
    }
  };

  Object.entries(obj).forEach(([key, value]) => set(res, key, value));

  return res;
};

type ErrorCode = 400 | 401 | 402 | 403 | 404;
// TODO: Remove ApiError and use src/lib/responses.ts
export class ApiError extends Error {
  code: ErrorCode;
  errors: Record<string, string>;

  constructor(code: ErrorCode, errors: Record<string, string> = {}) {
    super(`Status code: ${code} with ${JSON.stringify(errors)}`);

    this.code = code;
    this.errors = errors;
  }

  merge = (e: ApiError) => {
    this.errors = { ...this.errors, ...e.errors };
    return this;
  };

  set = (key: string, value: string) => {
    logError(`[${key}] ${value}`);
    this.errors[key] = value;
    return this;
  };

  setNotFound = () => this.setOther('No se encontró el recurso');

  setOther = (value: string) => this.set('other', value);

  setUnauthorized = () =>
    this.set(
      'unauthorized',
      'No tiene authorización, por favor verifique su sesión y vuelva a intentarlo'
    );

  setZodError = (value: ZodError) => {
    value.issues.forEach((e) => (this.errors[e.path.join('.')] = e.message));
    logError(this.errors);
    return this;
  };

  toError = () => error(this.code, this);

  toResponse = () => json({ errors: this.errors }, { status: this.code });

  toFail = () => fail(this.code, this.errors);
}

export const slugify = (str: string) => {
  str = str.replace(/^\s+|\s+$/g, ''); // trim leading/trailing white space
  str = str.toLowerCase(); // convert string to lowercase
  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove any non-alphanumeric characters
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-'); // remove consecutive hyphens
  return str;
};

export interface IGrid {
  total_rows: number;
  total_cols: number;
  current_row: number;
  current_col: number;
  editing: boolean;
}

export class Grid {
  internal: Writable<IGrid>;

  constructor(total_rows: number, total_cols: number) {
    this.internal = writable({
      total_rows,
      total_cols,
      current_row: 0,
      current_col: 0,
      editing: false
    });
  }

  get = () => this.internal;

  setEditing = (canEdit: boolean) => {
    this.internal.update((g) => ({
      ...g,
      editing: canEdit
    }));
  };

  navigatePreviousColumn = () => {
    this.internal.update((g) => {
      if (g.current_col > 0) {
        g.current_col -= 1;
      }
      return g;
    });
  };

  navigateNextColumn = () => {
    this.internal.update((g) => {
      if (g.current_col < g.total_cols - 1) {
        g.current_col += 1;
      }
      return g;
    });
  };

  navigatePreviousRow = () => {
    this.internal.update((g) => {
      if (g.current_row > 0) {
        g.current_row -= 1;
      }
      return g;
    });
  };

  navigateNextRow = () => {
    this.internal.update((g) => {
      if (g.current_row < g.total_rows - 1) {
        g.current_row += 1;
      }
      return g;
    });
  };

  navigateFirstRow = () => {
    this.internal.update((g) => {
      g.current_row = 0;
      return g;
    });
  };

  navigateLastRow = () => {
    this.internal.update((g) => {
      g.current_row = g.total_rows - 1;
      return g;
    });
  };

  navigateFirstColumn = () => {
    this.internal.update((g) => {
      g.current_col = 0;
      return g;
    });
  };

  navigateLastColumn = () => {
    this.internal.update((g) => {
      g.current_col = g.total_cols - 1;
      return g;
    });
  };
}

export type EditorOptions<T> = {
  error: string | string[] | null;
  focus: boolean;
  navigate: (direction: 'next' | 'previous') => void;
  stopEditing: () => void;
  tabindex: number;
  update: (value: T | null) => void;
  value: T | null;
};

export const normalizeLots = <T extends { images: JsonValue; }>(lot: T[]) => lot.map(normalizeLot);

export const normalizeLot = <T extends { images: JsonValue; }>(lot: T) => {
  let images: string[] = [];

  if (Array.isArray(lot.images)) {
    for (const image of lot.images) {
      if (typeof image === 'string') {
        images.push(image);
      }
    }
  }

  return lot;
};

export const logError = (e: unknown) => {
  console.error(e);
  if (e instanceof Error) {
    captureException(e);
  } else {
    captureMessage(JSON.stringify(e), 'error');
  }
};

export const isMobile = (userAgent: string) => {
  const regex = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  return regex.test(userAgent);
};

export const calculateTotals = (netAmount: bigint, tax_percentage: number | null, commission_percentage: number | null) => {
  const commission = commission_percentage ? (BigInt(netAmount) * BigInt(commission_percentage)) / 100n : 0n;
  const tax = tax_percentage ? (commission * BigInt(tax_percentage)) / 100n : 0n;
  const total = BigInt(netAmount) + tax + commission;

  return {
    tax,
    commission,
    total
  };
};

type TImageSize = 128 | 256 | 384 | 592 | 640 | 750 | 828 | 1080;
export const IMAGE_SIZES: TImageSize[] = [128, 256, 384, 592, 640, 750, 828, 1080];

export const getCloudflareImageVariant = (image: string | JsonValue, variant: TImageSize): string | null => {
  if (Array.isArray(image)) {
    return getCloudflareImageVariant(image[0], variant);
  } else if (typeof image !== 'string') {
    return null;
  } else {
    try {
      const parsed = JSON.parse(String(image));
      return getCloudflareImageVariant(parsed, variant);
    } catch (e) {
      return `https://imagedelivery.net/${PUBLIC_CLOUDFLARE_ACCOUNT_HASH}/${image}/${variant}x${variant}`;
    }
  }
};

type IFleatureFlags =
  | 'dashboard-v2'
  | 'live_auctions'
  | 'lot_bid_polling'
  | 'passkey'
  | 'real_state'
  | 'sms_twilio'
  | 'webauthn'
  | 'workflow_list';

export const flipper = (feature: IFleatureFlags) => env.PUBLIC_FEATURE_FLAGS?.includes(feature) ?? false;

export const stripSpecialCharacters = (str: string) => {
  // Stript accents, emojis and special characters, except spaces, numbers and letters, punctuation and url characters
  return str
    .normalize('NFD')
    .replaceAll(/[^\p{L}\p{N}\p{P}\p{Z}\p{S}]/gu, '')
    .replaceAll(/\s+/g, ' ')
    .replaceAll(/[\u0300-\u036f]/gu, '')
    .trim();
};

export const getFilesFromEvent = (callback: (files: File[]) => void) => {
  return (e: { target?: { files?: File[]; }; } | { detail: { acceptedFiles?: File[]; }; }) => {
    if ('detail' in e) {
      callback(e.detail.acceptedFiles ?? []);
    } else {
      callback(e.target?.files ?? []);
    }
  };
};

// This function is used to normalize the date in the server, in the frontend we use the date as it is
export const normalizeDate = (date: Date) => {
  // if running on the server, return the date with a fixed offset
  if (typeof window == 'undefined') {
    const now = new Date();
    let offsetHour;

    // check if is summer time
    if (now.getMonth() > 9 || now.getMonth() < 2) {
      // In Paraguay, the offset is -4 hours
      offsetHour = 4;
    } else {
      // In Paraguay, the offset is -3 hours
      offsetHour = 3;
    }


    // subtract offset to date
    const offset = offsetHour * 60 * 60 * 1000;
    const offsetDate = new Date(date.getTime() - offset);
    console.log(`[normalizeDate] offsetHour: ${offsetHour} offset: ${offset} offsetDate: ${offsetDate} date: ${date}`);
    return offsetDate;
  } else {
    return date;
  }
};

export const instrumented = async <T>(name: string, fn: () => T): Promise<T> => {
  console.log(`[instrumented] ${name} started`);
  const start = Date.now();
  const res = await fn();
  const elapsed = Date.now() - start;
  console.log(`[instrumented] ${name} took ${elapsed}ms`);
  return res;
};

export const instrumentedSync = <T>(name: string, fn: () => T): T => {
  console.log(`[instrumented] ${name} started`);
  const start = Date.now();
  const res = fn();
  const elapsed = Date.now() - start;
  let elapsedUnit;

  if (elapsed > 60000) {
    elapsedUnit = `${(elapsed / 60000).toFixed(2)}m`;
  } else if (elapsed > 1000) {
    elapsedUnit = `${(elapsed / 1000).toFixed(2)}s`;
  } else {
    elapsedUnit = `${elapsed}ms`;
  }
  console.log(`[instrumented] ${name} took ${elapsedUnit}`);
  return res;
};

// This is a workaround to avoid the bigint serialization issue
(BigInt.prototype as any).toJSON = function () {
  return Number(this);
};

const encoder = new TextEncoder();
export function json(data: any, init: ResponseInit = {}) {
  // TODO deprecate this in favour of `Response.json` when it's
  // more widely supported
  const body = JSON.stringify(data);

  // we can't just do `text(JSON.stringify(data), init)` because
  // it will set a default `content-type` header. duplicated code
  // means less duplicated work
  const headers = new Headers(init?.headers);
  if (!headers.has('content-length')) {
    headers.set('content-length', encoder.encode(body).byteLength.toString());
  }

  if (!headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  return new Response(body, {
    ...init,
    headers
  });
}

/**
 * This function is used to check if the lot is about to end
 * and if so, it will call the onShouldUpdate function every 10 seconds
 * until the lot ends. Also it listens the websocket for the lot updates
 * and if the lot is updated, it will call the onShouldUpdate function.
 * 
 * @param lot 
 * @param onShouldUpdate 
 */
export const shouldLotBeUpdated = (lot: Pick<lots, 'uid' | 'end_date' | 'status'>, onShouldUpdate: () => void) => {
  if (lot.status != 'ongoing' || !browser) return () => { };

  const ws = websocketClient();
  ws.send({ subscribe: `lots:${lot.uid}` });
  const unsub = ws.onLotUpdated(onShouldUpdate);

  let interval: any = null;

  if (flipper('lot_bid_polling')) {
    // Check if the lot is about to end
    const now = new Date();
    const endDate = new Date(lot.end_date);
    const diff = endDate.getTime() - now.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);


    if (minutes <= 5) {
      interval = setInterval(() => {
        onShouldUpdate();
      }, (minutes == 0 && seconds <= 30) ? 1000 : 10000);
    }
  }

  return () => {
    Boolean(interval) && clearInterval(interval);
    unsub();
  };
};